import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import StoreSubnav from "@/components/store/StoreSubnav";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, ImageIcon, ShoppingCart } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useStoreCart, formatPrice } from "@/store/storeCart";
import { toast } from "sonner";

type Variant = {
  id: string;
  title: string | null;
  price_cents: number;
  stock_quantity: number | null;
  unlimited_stock: boolean;
  active: boolean;
};
type Product = {
  id: string;
  slug: string;
  title: string;
  subtitle: string | null;
  description: string | null;
  base_price_cents: number;
  currency: string;
  images: string[] | null;
  product_type: "physical" | "digital";
  stock_quantity: number | null;
  unlimited_stock: boolean;
  low_stock_threshold: number | null;
  vendor: {
    id: string;
    slug: string;
    display_name: string;
    identity_verified: boolean;
  };
  variants: Variant[];
};

const StoreProductPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const add = useStoreCart((s) => s.add);

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data } = await supabase
        .from("store_products")
        .select(
          `id, slug, title, subtitle, description, base_price_cents, currency, images, product_type,
           stock_quantity, unlimited_stock, low_stock_threshold,
           vendor:store_vendors_public!vendor_id(id, slug, display_name, identity_verified),
           variants:store_product_variants(id, title, price_cents, stock_quantity, unlimited_stock, active)`,
        )
        .eq("slug", slug)
        .eq("status", "active")
        .maybeSingle();
      if (cancelled) return;
      const p = data as unknown as Product | null;
      setProduct(p);
      const firstActiveVariant = p?.variants?.find((v) => v.active);
      setVariantId(firstActiveVariant?.id ?? null);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const selectedVariant = useMemo(
    () => product?.variants?.find((v) => v.id === variantId) ?? null,
    [product, variantId],
  );
  const unitPrice = selectedVariant?.price_cents ?? product?.base_price_cents ?? 0;
  const hasVariants = (product?.variants?.filter((v) => v.active).length ?? 0) > 0;
  const inStock = selectedVariant
    ? selectedVariant.unlimited_stock ||
      (selectedVariant.stock_quantity != null && selectedVariant.stock_quantity > 0)
    : product
      ? product.unlimited_stock ||
        (product.stock_quantity != null && product.stock_quantity > 0)
      : true;
  const productLowStock =
    !!product &&
    !hasVariants &&
    !product.unlimited_stock &&
    product.stock_quantity != null &&
    product.stock_quantity > 0 &&
    product.stock_quantity <= (product.low_stock_threshold ?? 5);

  const handleAdd = () => {
    if (!product) return;
    add({
      product_id: product.id,
      variant_id: variantId,
      vendor_id: product.vendor.id,
      vendor_slug: product.vendor.slug,
      vendor_name: product.vendor.display_name,
      product_slug: product.slug,
      title: product.title,
      variant_title: selectedVariant?.title ?? null,
      unit_price_cents: unitPrice,
      currency: product.currency,
      quantity: 1,
      image_url: product.images?.[0] ?? null,
      product_type: product.product_type,
      max_stock:
        selectedVariant && !selectedVariant.unlimited_stock
          ? selectedVariant.stock_quantity ?? null
          : !selectedVariant && product && !product.unlimited_stock
            ? product.stock_quantity ?? null
            : null,
    });
    toast.success("Added to cart", {
      action: { label: "View cart", onClick: () => navigate("/store/cart") },
    });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <StoreSubnav />
      <main className="flex-1 mx-auto w-full max-w-6xl px-6 py-8">
        <Link to="/store" className="text-sm text-muted-foreground hover:underline">
          ← Back to Store
        </Link>

        {loading ? (
          <div className="mt-6 grid md:grid-cols-2 gap-8">
            <Skeleton className="aspect-square rounded-lg" />
            <div className="space-y-3">
              <Skeleton className="h-8 w-3/4" />
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-24 w-full" />
            </div>
          </div>
        ) : !product ? (
          <Card className="p-12 text-center mt-6">
            <h1 className="text-xl font-semibold">Product not found</h1>
            <p className="text-sm text-muted-foreground mt-2">
              It may have been removed or is no longer available.
            </p>
          </Card>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 gap-10">
            <div>
              <div className="aspect-square rounded-lg overflow-hidden bg-muted/50 flex items-center justify-center">
                {product.images?.[activeImg] ? (
                  <img
                    src={product.images[activeImg]}
                    alt={product.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <ImageIcon className="h-16 w-16 text-muted-foreground/40" />
                )}
              </div>
              {product.images && product.images.length > 1 && (
                <div className="mt-3 grid grid-cols-5 gap-2">
                  {product.images.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveImg(i)}
                      className={`aspect-square rounded overflow-hidden border-2 ${
                        i === activeImg ? "border-primary" : "border-transparent"
                      }`}
                    >
                      <img src={url} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Link
                to={`/store/vendor/${product.vendor.slug}`}
                className="text-sm text-muted-foreground hover:underline inline-flex items-center gap-1"
              >
                {product.vendor.display_name}
                {product.vendor.identity_verified && (
                  <ShieldCheck className="h-4 w-4 text-primary" />
                )}
              </Link>
              <h1 className="mt-2 text-3xl font-bold">{product.title}</h1>
              {product.subtitle && (
                <p className="mt-1 text-muted-foreground">{product.subtitle}</p>
              )}
              <Badge className="mt-2 bg-amber-500/90 text-white text-[10px] uppercase tracking-wider hover:bg-amber-500">
                Demo Product
              </Badge>

              <p className="mt-4 text-2xl font-semibold">
                {formatPrice(unitPrice, product.currency)}
              </p>
              <Badge variant="secondary" className="mt-2">
                {product.product_type === "digital" ? "Digital download" : "Physical item"}
              </Badge>
              {productLowStock && (
                <Badge variant="outline" className="mt-2 ml-2 text-amber-600 border-amber-600">
                  Only {product.stock_quantity} left
                </Badge>
              )}
              {!inStock && !hasVariants && (
                <Badge variant="destructive" className="mt-2 ml-2">
                  Out of stock
                </Badge>
              )}

              {product.variants?.filter((v) => v.active).length > 0 && (
                <div className="mt-6">
                  <p className="text-sm font-medium mb-2">Options</p>
                  <div className="flex flex-wrap gap-2">
                    {product.variants
                      .filter((v) => v.active)
                      .map((v) => (
                        <button
                          key={v.id}
                          onClick={() => setVariantId(v.id)}
                          className={`px-3 py-2 rounded-md border text-sm transition-colors ${
                            variantId === v.id
                              ? "border-primary bg-primary/10"
                              : "border-border hover:border-foreground/40"
                          }`}
                        >
                          {v.title ?? "Option"}
                          {!v.unlimited_stock && v.stock_quantity != null && v.stock_quantity <= 5 && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              {v.stock_quantity} left
                            </span>
                          )}
                        </button>
                      ))}
                  </div>
                </div>
              )}

              <div className="mt-8 flex gap-3">
                <Button size="lg" onClick={handleAdd} disabled={!inStock} className="flex-1">
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  {inStock ? "Add to cart" : "Out of stock"}
                </Button>
              </div>

              {product.description && (
                <div className="mt-8 prose prose-sm dark:prose-invert max-w-none">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                    Details
                  </h3>
                  <p className="whitespace-pre-wrap">{product.description}</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
      <SiteFooter />
    </div>
  );
};

export default StoreProductPage;
